import { useEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "../../platform/motion/usePrefersReducedMotion";
import { AVATAR_POSTER_URL, type AvatarGesture } from "./avatar-clips";
import { reactions } from "./reaction-bus";
import type { AvatarScene } from "./avatar-scene";
import "./avatar.css";

export type AvatarState = "loading" | "ready" | "unavailable";

type Props = {
  /** The gesture to play, and a number that changes each time it should play again. */
  gesture: { name: AvatarGesture; token: number } | null;
  onStateChange: (state: AvatarState) => void;
};

/**
 * Orla herself. A still poster of the same frame shows at once; the 3D scene is loaded as
 * its own chunk and takes over when ready. If WebGL or the model is unavailable the poster
 * simply stays, so the page never shows an empty box.
 */
export function OrlaAvatar({ gesture, onStateChange }: Props) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const scene = useRef<AvatarScene | null>(null);
  const [state, setState] = useState<AvatarState>("loading");
  const reducedMotion = usePrefersReducedMotion();
  const report = useRef(onStateChange);

  useEffect(() => {
    report.current = onStateChange;
  }, [onStateChange]);

  useEffect(() => {
    const element = canvas.current;
    if (!element) return;
    const controller = new AbortController();
    let sizeObserver: ResizeObserver | null = null;
    let viewObserver: IntersectionObserver | null = null;
    const onVisibility = () => scene.current?.setRunning(document.visibilityState === "visible");

    import("./avatar-scene")
      .then(({ createAvatarScene }) =>
        createAvatarScene({ canvas: element, animate: !reducedMotion, signal: controller.signal }),
      )
      .then((created) => {
        scene.current = created;
        sizeObserver = new ResizeObserver(([entry]) => {
          if (entry) created.resize(entry.contentRect.width, entry.contentRect.height);
        });
        sizeObserver.observe(element);
        viewObserver = new IntersectionObserver(([entry]) => {
          created.setRunning(
            Boolean(entry?.isIntersecting) && document.visibilityState === "visible",
          );
        });
        viewObserver.observe(element);
        document.addEventListener("visibilitychange", onVisibility);
        setState("ready");
        report.current("ready");
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error("Orla's 3D scene could not start; the poster stays.", error);
        setState("unavailable");
        report.current("unavailable");
      });

    return () => {
      controller.abort();
      sizeObserver?.disconnect();
      viewObserver?.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      scene.current?.dispose();
      scene.current = null;
    };
  }, [reducedMotion]);

  useEffect(() => {
    if (!gesture || state !== "ready") return;
    void scene.current?.gesture(gesture.name);
  }, [gesture, state]);

  // Reactions to the rest of the page: a hovered link, a focused button.
  useEffect(() => {
    if (state !== "ready") return;
    return reactions.subscribe((reaction) => {
      const active = scene.current;
      if (!active) return;
      if (reaction.kind === "gesture") void active.gesture(reaction.name);
      if (reaction.kind === "hold") active.hold(reaction.name);
      if (reaction.kind === "release") active.release();
    });
  }, [state]);

  return (
    <div className="orla-avatar" data-avatar-state={state}>
      <img
        className="orla-avatar__poster"
        src={AVATAR_POSTER_URL}
        alt="Orla, a young woman with auburn hair in a bun, wearing a black and teal flight suit, smiling."
        width={885}
        height={1319}
        decoding="async"
      />
      <canvas ref={canvas} className="orla-avatar__canvas" aria-hidden="true" />
    </div>
  );
}
